import { useState, useEffect, useMemo, useRef } from "react";
import { BarChart3, Settings, MapPin, Cog, ChevronRight, ClipboardList as ClipboardListIcon, CheckCircle2, AlertTriangle, AlertOctagon, HelpCircle, FileDown, FileSpreadsheet, Sparkles, FileText } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import FoldBlock from "../customer-blocks/FoldBlock";
import PerfCapturePanel from "./PerfCapturePanel";
import PerfConfigDrawer from "./PerfConfigDrawer";
import PerfRegisterView from "./PerfRegisterView";
import SiteTrendHistory from "./SiteTrendHistory";
import PlantReportEmbed from "./PlantReportEmbed";
import { toast } from "sonner";

/**
 * PerformanceBlock — Iter 367 (Performance Phase B)
 *
 * Renders per customer:
 *   Sites → Equipment → Parameters → Daily capture + History.
 *
 * Reusable across:
 *   - CustomerDetail's "Performance" tab (canManage=true for admins)
 *   - ClientPortal's "/portal/performance" view (canManage=false)
 *   - Standalone /performance page (uses ?customer= param)
 */
export default function PerformanceBlock({ customerId, canEdit = false, canCapture = true, defaultOpen = true, isPortal = false, deepLink = null, visibleSection = "all" }) {
  const { user } = useAuth();
  // Iter 392 — Techs (staff/technician) get log-sheet access by
  // default. The Owner can flip the `perf.analytics.view` permission
  // OFF per-user in Settings → Users to hide the Analytics fold from
  // that tech. Owner/admin always see everything.
  const role = (user?.role || "").toLowerCase();
  const isOwnerish = role === "owner" || role === "admin";
  const analyticsBlocked = !isOwnerish
    && user?.permissions
    && user.permissions["perf.analytics.view"] === false;
  const showAnalytics = !analyticsBlocked;
  const [sites, setSites] = useState([]);
  const [equipmentBySite, setEquipmentBySite] = useState({});
  const [parametersByEq, setParametersByEq] = useState({});
  const [selectedEqId, setSelectedEqId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [configOpen, setConfigOpen] = useState(false);
  const [configSite, setConfigSite] = useState(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  // Iter 384 — replaced sub-tab strip with two side-by-side FoldBlocks
  // (Log sheet + Performance analytics) so the module mirrors the Work
  // tab's Job cards / Support tickets pattern.

  // Iter 415 (Dirk, 2026-02-28) — Prevent overlapping loadSites runs
  // (React StrictMode double-mount + customerId prop changes were
  // causing an older completion to overwrite the newer's equipment
  // map with an empty {} → the site card wrongly showed
  // "No equipment yet." even though the API returned rows.
  const loadCallIdRef = useRef(0);
  const loadSites = async () => {
    const callId = ++loadCallIdRef.current;
    setLoading(true);
    setErr("");
    try {
      const url = customerId ? `/perf/sites?customer_id=${customerId}` : "/perf/sites";
      const { data } = await api.get(url);
      if (callId !== loadCallIdRef.current) return; // superseded
      setSites(data);
      // pre-load equipment for each site (parallel; small counts)
      const eqMap = {};
      await Promise.all((data || []).map(async (s) => {
        try {
          const { data: eq } = await api.get(`/perf/equipment?site_id=${s.id}`);
          // Some endpoints return {items:[]} shape, others raw arrays.
          // Normalise both so downstream `.length` / `.map` never trip.
          eqMap[s.id] = Array.isArray(eq) ? eq : (eq?.items || []);
        } catch (_) { eqMap[s.id] = []; }
      }));
      if (callId !== loadCallIdRef.current) return; // superseded — don't clobber newer state
      setEquipmentBySite(eqMap);
      // auto-select first equipment
      const firstEq = Object.values(eqMap).flat()[0];
      if (firstEq && !selectedEqId) setSelectedEqId(firstEq.id);
    } catch (e) {
      if (callId !== loadCallIdRef.current) return;
      setErr(formatErr(e));
    } finally {
      if (callId === loadCallIdRef.current) setLoading(false);
    }
  };

  const loadParams = async (eqId) => {
    if (!eqId) return;
    try {
      const { data } = await api.get(`/perf/parameters?equipment_id=${eqId}`);
      setParametersByEq((s) => ({ ...s, [eqId]: data }));
    } catch (_) { /* noop */ }
  };

  useEffect(() => { loadSites(); }, [customerId]);
  useEffect(() => { if (selectedEqId) loadParams(selectedEqId); }, [selectedEqId, historyRefreshKey]);

  const selectedEquipment = useMemo(() => {
    for (const list of Object.values(equipmentBySite)) {
      const f = (list || []).find((e) => e.id === selectedEqId);
      if (f) return f;
    }
    return null;
  }, [equipmentBySite, selectedEqId]);

  const activeParams = parametersByEq[selectedEqId] || [];
  // Iter 394c — Calculated params (deltas, ratios, sums) are
  // auto-computed and should NEVER show in the Log Sheet for input.
  // They still appear in reports, graphs, and Excel exports because
  // those code paths run the lazy resolver on the fly.
  const visibleParams = activeParams.filter((p) => p.kind !== "calculated");

  // Iter 391b — Determine the selected site (for site-wide completion
  // and prev/next equipment nav).
  const selectedSite = useMemo(() => {
    if (!selectedEquipment) return null;
    return sites.find((s) => s.id === selectedEquipment.site_id) || null;
  }, [sites, selectedEquipment]);

  // Iter 391b — Compute prev/next equipment WITHIN the same site so
  // techs can walk equipment without scrolling back to the site nav.
  const siteEquipmentList = selectedSite ? (equipmentBySite[selectedSite.id] || []) : [];
  const siteEqIdx = siteEquipmentList.findIndex((e) => e.id === selectedEqId);
  const prevEquipment = siteEqIdx > 0 ? siteEquipmentList[siteEqIdx - 1] : null;
  const nextEquipment = siteEqIdx >= 0 && siteEqIdx < siteEquipmentList.length - 1
    ? siteEquipmentList[siteEqIdx + 1]
    : null;

  const totalEquipment = Object.values(equipmentBySite).reduce((s, l) => s + (l?.length || 0), 0);
  const analyticsCount = totalEquipment; // headline chip on the analytics fold

  // ── Iter 384 — Log sheet body (daily capture) ────────────────
  const logSheetBody = (
    <div className="space-y-3">
      {loading ? (
        <div className="text-xs text-[#647087]">Loading…</div>
      ) : sites.length === 0 ? (
        <NoSitesEmpty customerId={customerId} canEdit={canEdit} isPortal={isPortal} />
      ) : (
        <div className="grid md:grid-cols-[minmax(0,240px)_minmax(0,1fr)] gap-3">
          {/* Left rail: sites & equipment */}
          <aside className="space-y-3">
            {sites.map((site) => {
              const eqs = equipmentBySite[site.id] || [];
              return (
                <div key={site.id} className="hv-card p-2" data-testid={`perf-site-${site.id}`} data-perf-site-anchor={site.id}>
                  <div className="px-2 py-1 flex items-center justify-between gap-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <MapPin className="w-3.5 h-3.5 text-[#1fb6c1] shrink-0" />
                      <div className="text-sm font-medium truncate">{site.name}</div>
                      {site.deleted && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-400/30 shrink-0" title="This site was deleted from the customer profile but still has equipment set up here. Restore it from the Trash to keep using it." data-testid={`perf-site-deleted-${site.id}`}>
                          Deleted site, still has equipment
                        </span>
                      )}
                    </div>
                    {canEdit && !isPortal && (
                      <button
                        type="button"
                        onClick={() => { setConfigSite(site); setConfigOpen(true); }}
                        className="p-1 rounded hover:bg-white/5 text-[#a0aabf]"
                        title="Configure equipment & parameters"
                        data-testid={`perf-site-cfg-${site.id}`}
                      >
                        <Settings className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="space-y-1 mt-1">
                    {eqs.length === 0 && (
                      <div className="text-[10px] text-[#647087] px-2 py-1">No equipment yet.</div>
                    )}
                    {eqs.map((eq) => (
                      <button
                        key={eq.id}
                        type="button"
                        onClick={() => setSelectedEqId(eq.id)}
                        className={`w-full text-left px-2 py-1.5 rounded-md text-xs flex items-center justify-between gap-1 transition ${selectedEqId === eq.id ? "bg-[#1fb6c1]/15 text-white border border-[#1fb6c1]/40" : "text-[#a0aabf] hover:bg-white/[0.04] border border-transparent"}`}
                        data-testid={`perf-eq-${eq.id}`}
                      >
                        <span className="flex items-center gap-1.5 min-w-0">
                          <Cog className="w-3 h-3 shrink-0" /> <span className="truncate">{eq.name}</span>
                        </span>
                        <ChevronRight className="w-3 h-3 opacity-50 shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </aside>
          {/* Right pane: capture only. */}
          <div className="space-y-3 min-w-0">
            {selectedEquipment ? (
              canCapture && (
                <PerfCapturePanel
                  equipment={selectedEquipment}
                  parameters={visibleParams}
                  onSaved={() => setHistoryRefreshKey((k) => k + 1)}
                  canCapture={canCapture}
                  site={selectedSite}
                  onPrevEquipment={prevEquipment ? () => setSelectedEqId(prevEquipment.id) : null}
                  onNextEquipment={nextEquipment ? () => setSelectedEqId(nextEquipment.id) : null}
                  prevEquipmentName={prevEquipment?.name}
                  nextEquipmentName={nextEquipment?.name}
                />
              )
            ) : (
              <div className="hv-card p-6 text-center text-sm text-[#647087]">
                Select equipment from the left to capture readings.
              </div>
            )}
          </div>
        </div>
      )}
      {err && <div className="hv-card border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">{err}</div>}
    </div>
  );

  // ── Iter 384 — Performance analytics body (register + trend) ─
  const analyticsBody = (
    <div className="space-y-3">
      {canEdit && !isPortal && sites.length > 0 && (
        <ZeroBackfillButton onDone={() => setHistoryRefreshKey((k) => k + 1)} />
      )}
      {loading ? (
        <div className="text-xs text-[#647087]">Loading…</div>
      ) : sites.length === 0 ? (
        <NoSitesEmpty customerId={customerId} canEdit={canEdit} isPortal={isPortal} />
      ) : (
        <>
          {sites.map((site) => {
            const filter = deepLink && (deepLink.site === site.id) ? {
              equipmentId: deepLink.equipment,
              parameterId: deepLink.parameter,
              date: deepLink.date,
            } : null;
            return (
              <PerfRegisterView
                key={`site-reg-${site.id}`}
                site={site}
                equipmentBySite={equipmentBySite}
                initialFilter={filter}
              />
            );
          })}
          {sites.map((site) => (
            <SiteTrendHistory key={`site-trend-${site.id}`} site={site} equipmentBySite={equipmentBySite} />
          ))}
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-3" data-testid="perf-blocks">
      {/* Iter 400.21 — `visibleSection` allows callers (Performance.jsx
          top tabs + CustomerDetail Performance sub-tabs) to render only
          the Logbook OR only Analytics. Default "all" keeps the legacy
          two-fold-block layout. */}
      {(visibleSection === "all" || visibleSection === "logbook") && (
        <FoldBlock
          icon={ClipboardListIcon}
          iconColor="#1FB6C1"
          title="Log sheet"
          count={totalEquipment}
          defaultOpen={defaultOpen}
          testId="block-perf-logsheet"
          emptyHint="No equipment configured yet."
          alwaysRenderChildren
        >
          {logSheetBody}
        </FoldBlock>
      )}

      {showAnalytics && (visibleSection === "all" || visibleSection === "analytics") && (
        <FoldBlock
          icon={BarChart3}
          iconColor="#A78BFA"
          title="Performance analytics"
          count={analyticsCount}
          defaultOpen={visibleSection === "analytics" ? true : false}
          testId="block-perf-analytics"
          emptyHint="No performance data captured yet."
          alwaysRenderChildren
        >
          {analyticsBody}
        </FoldBlock>
      )}

      {/* Iter 418.ap (Dirk, 2026-02-28) — Plant Report embedded in the
          customer's Performance tab so the operator doesn't have to
          hop back to /performance to see AI Insights, trend charts
          and the branded PDF for this customer specifically. Portal
          clients don't see this fold (CRM only). */}
      {!isPortal && (visibleSection === "all" || visibleSection === "report") && customerId && (
        <FoldBlock
          icon={FileText}
          iconColor="#10B981"
          title="Plant Report"
          defaultOpen={visibleSection === "report"}
          testId="block-perf-plant-report"
          alwaysRenderChildren
        >
          <PlantReportEmbed customerId={customerId} customerName={null} />
        </FoldBlock>
      )}

      {configOpen && configSite && (
        <PerfConfigDrawer
          open={configOpen}
          onClose={() => setConfigOpen(false)}
          site={configSite}
          onChanged={() => {
            loadSites();
            if (selectedEqId) loadParams(selectedEqId);
          }}
        />
      )}
    </div>
  );
}

function NoSitesEmpty({ customerId, canEdit, isPortal }) {
  return (
    <div className="hv-card p-4 space-y-2" data-testid="perf-block-no-sites">
      <div className="text-sm text-[#a0aabf]">
        {customerId
          ? "No sites on this customer yet. Add a site under the customer's Sites tab first — then attach equipment here to start capturing readings."
          : "No sites available. Add sites under the Customers pillar first."}
      </div>
      {canEdit && customerId && !isPortal && (
        <a
          href={`/customers/${customerId}?tab=sites`}
          className="hv-btn hv-btn-primary text-xs inline-flex"
          data-testid="perf-block-goto-sites"
        >
          <MapPin className="w-3.5 h-3.5" /> Open Sites tab
        </a>
      )}
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    ok: { c: "#10b981", Icon: CheckCircle2, label: "OK" },
    warn: { c: "#f59e0b", Icon: AlertTriangle, label: "WARN" },
    alarm: { c: "#f43f5e", Icon: AlertOctagon, label: "ALARM" },
  };
  const m = map[status] || { c: "#647087", Icon: HelpCircle, label: "?" };
  return (
    <span
      className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold inline-flex items-center gap-0.5"
      style={{ background: `${m.c}1a`, color: m.c, border: `1px solid ${m.c}55` }}
    >
      <m.Icon className="w-2.5 h-2.5" /> {m.label}
    </span>
  );
}

function PerfExportBar({ equipmentId, equipmentName }) {
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(null);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const { data } = await api.get(`/perf/report/${fmt}?equipment_id=${equipmentId}&days=${days}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `perf-${(equipmentName || "equipment").replace(/\s+/g, "_")}-${days}d.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error(`Export failed: ${formatErr(e)}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="hv-card px-3 py-2 flex items-center justify-between flex-wrap gap-2" data-testid="perf-export-bar">
      <div className="text-xs text-[#a0aabf]">
        Export log book for <b className="text-white">{equipmentName}</b>
      </div>
      <div className="flex items-center gap-2">
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="hv-input text-xs py-1"
          data-testid="perf-export-range"
        >
          <option value={7}>7 days</option>
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
          <option value={180}>180 days</option>
        </select>
        <button
          type="button"
          onClick={() => download("xlsx")}
          disabled={busy !== null}
          className="hv-btn hv-btn-ghost text-xs disabled:opacity-50"
          data-testid="perf-export-xlsx"
        >
          <FileSpreadsheet className="w-3.5 h-3.5" /> {busy === "xlsx" ? "…" : "Excel"}
        </button>
        <button
          type="button"
          onClick={() => download("pdf")}
          disabled={busy !== null}
          className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
          data-testid="perf-export-pdf"
        >
          <FileDown className="w-3.5 h-3.5" /> {busy === "pdf" ? "…" : "PDF"}
        </button>
      </div>
    </div>
  );
}


/**
 * Iter 389 — ZeroBackfillButton.
 * Owner/Admin one-click sweep to clear all historical zero-value
 * warn/alarm readings (soft-deleted via `superseded_by`). Non-destructive —
 * rows remain in Mongo for audit, but are hidden from charts and PDFs.
 */
function ZeroBackfillButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/perf/readings/backfill-zeros", {});
      if (data.matched === 0) {
        toast.success("No zero-value warnings found — history is already clean.");
      } else {
        toast.success(`Swept ${data.swept} zero-value warning${data.swept === 1 ? "" : "s"} from history.`);
      }
      setConfirm(false);
      onDone?.();
    } catch (e) {
      toast.error(`Backfill failed: ${formatErr(e)}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="hv-card p-2.5 flex items-center justify-between gap-3 border-white/5">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-3.5 h-3.5 text-[#a78bfa] shrink-0" />
          <div className="text-[11px] text-[#a0aabf] truncate">
            Zero-value cleanup — hide historical readings where techs typed <b className="text-white font-mono">0</b> while equipment was off.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setConfirm(true)}
          className="hv-btn hv-btn-ghost text-[10px] shrink-0"
          data-testid="perf-zero-backfill-btn"
        >
          Clean zero warnings
        </button>
      </div>
      {confirm && (
        <div
          className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !busy && setConfirm(false)}
          data-testid="perf-zero-backfill-modal"
        >
          <div
            className="hv-card max-w-md w-full p-4 space-y-3 border-amber-400/40 bg-[#0f1420]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-white">Clean zero-value warnings?</div>
                <div className="text-[11px] text-[#a0aabf] mt-1 leading-relaxed">
                  This will scan every historical reading in your tenant and soft-delete any
                  where the value is <b className="text-white font-mono">0</b> (or near-zero) AND the status
                  is <b className="text-white">warn/alarm</b>. Rows stay in the database
                  (auditable via <span className="font-mono">superseded_by = &quot;iter389_zero_backfill&quot;</span>)
                  but no longer show up in charts, PDFs, or notification history.
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-1 border-t border-white/5">
              <button
                type="button"
                onClick={() => setConfirm(false)}
                disabled={busy}
                className="hv-btn hv-btn-ghost text-xs disabled:opacity-40"
                data-testid="perf-zero-backfill-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={run}
                disabled={busy}
                className="hv-btn hv-btn-primary text-xs disabled:opacity-40"
                data-testid="perf-zero-backfill-confirm"
              >
                {busy ? "Sweeping…" : "Yes, sweep now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
